import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { settings, shipment } from '../../../models/entities/shipment/shipment';
import { ColumnType } from '../Grid/types/Column';
import ShipmentRoute from '../SmallComponents/ShipmentRoute';
import ShipmentTypeWithTooltip from '../SmallComponents/ShipmentTypeWithTooltip';
import ShipmentWarning from '../Grid/specialColumns/ShipmentWarning';
import carriers from '../../../static/data/carriers.json';

type BoardDragOverlayItemProps = {
    item: shipment;
    gridColumns: Array<ColumnType<shipment>>;
    settings?: settings;
};

export const BoardDragOverlayItem = React.memo(({ item, gridColumns, settings }: BoardDragOverlayItemProps) => {
    // Pre-calculate values to avoid recalculation during drag
    const itemColor = React.useMemo(() => {
        if (item.state === 'DRAFT') return '#b2b2b2';
        switch (item.alertStatus) {
            case null:
            case 3:
                return '#639e31';
            case 2:
                return '#f4c915';
            case 1:
                return '#c1292e';
            default:
                return '#639e31';
        }
    }, [item.state, item.alertStatus]);

    const ataOrEta = React.useMemo(() => {
        const value = item['ata'] || item['eta'];
        return value ? moment(value.toString()).format('DD/MM/YYYY HH:mm') : '-';
    }, [item.ata, item.eta]);

    const ataOrEtaLabel = React.useMemo(() => item['ata'] ? 'ATA' : 'ETA', [item.ata]);

    // Simplified details - only show essential info to reduce rendering overhead
    const essentialDetails = React.useMemo(() => {
        const fieldsToExclude = ['CargoZoneNumber', 'typeCode', 'originStation', 'destinationStation', 'state', 'alertStatus', 'ata', 'eta'];
        
        return gridColumns
            .filter((column) => {
                const key = column.field;
                const value = item[key];
                return column.isDisplayed !== false && value != null && value.toString().trim() != '' && !fieldsToExclude.includes(key);
            })
            .slice(0, 2) // Only show first 2 details for performance
            .map((column, i) => {
                let detailValue = item[column.field];
                
                // Simplified formatting for performance
                if (column?.columnType === 'date') {
                    detailValue = moment(detailValue?.toString()).format('DD/MM/YY');
                } else if (column?.columnType === 'date-time') {
                    detailValue = moment(detailValue?.toString()).format('DD/MM/YYYY HH:mm');
                }
                
                const bold = ['consigneeName', 'shipperName'].includes(column.field);
                return (
                    <div className="detail" key={i}>
                        <span className="detail-header">{column?.title}: </span>
                        <span className={`detail-value ${bold && 'bold'}`}>{detailValue}</span>
                    </div>
                );
            });
    }, [item, gridColumns]);

    return (
        <OverlayContainer>
            <StateColor color={itemColor} />
            <ItemContent>
                <ShipmentItemTitle>
                    <div className="shipment-icon">
                        <ShipmentTypeWithTooltip iconWidth={28} typeCode={item.typeCode} subTypeCode={item.subTypeCode} />
                    </div>
                    <span className="shipment-number">
                        #{item.CargoZoneNumber}
                    </span>
                    <div className="shipment-alert-icon">
                        <ShipmentWarning alertStatus={item.alertStatus} shipmentId={item.id} iconWidth={15} />
                    </div>
                </ShipmentItemTitle>
                <ShipmentRouteContainer>
                    <ShipmentRoute entity={item} flagSize="20px" fontSize="14px" fontWeight="300px" />
                </ShipmentRouteContainer>
                <Details>
                    <div className="detail">
                        <span className="detail-header">{ataOrEtaLabel}: </span>
                        <span className="detail-value">{ataOrEta}</span>
                    </div>
                    {essentialDetails}
                </Details>
            </ItemContent>
        </OverlayContainer>
    );
});

const OverlayContainer = styled.div`
    margin: 0 0 8px 0;
    background-color: #fff;
    display: flex;
    box-shadow: 0 6px 9px 0 rgba(0, 0, 0, 0.1);
    border: 1px solid #e4e4e4;
    width: 100%;
    z-index: 99999;
    position: relative;
    transform: rotate(2deg);
    opacity: 0.9;
    user-select: none;
    pointer-events: none;
    will-change: transform;
    backface-visibility: hidden;
    transform-style: preserve-3d;
`;

const StateColor = styled.div<{ color: string }>`
    width: 3px;
    background-color: ${(props) => props.color};
`;

const ItemContent = styled.div`
    flex: 1;
    height: 100%;
    padding: 12px;
`;

const Details = styled.div`
    .detail {
        margin: 4px 0;
        .detail-header {
            color: #394372;
        }
        .detail-value {
            color: #757575;
        }
        .bold {
            font-weight: bold;
        }
    }
`;

const ShipmentItemTitle = styled.div`
    display: flex;
    align-items: center;
    color: #394372;
    font-size: 14px;
    white-space: nowrap;

    .shipment-icon {
        margin-right: 10px;
    }
    .shipment-number {
        cursor: pointer;
        transition: color 0.1s;
        pointer-events: auto;
        z-index: 10;
        position: relative;
    }
    .shipment-alert-icon {
        margin-left: 10px;
    }
`;

const ShipmentRouteContainer = styled.div`
    margin-top: 10px;
`;
